/**
 * QAQCForms - Fillable PDF Forms for QA/QC Documentation
 * 
 * This component provides functionality to:
 * - List available QA/QC PDF forms
 * - View/fill PDF forms in browser
 * - Print filled forms
 * - Download filled forms
 * - Email filled forms
 * 
 * PDFs should be placed in: public/pdfs/qaqc/
 */

import React, { useState, useEffect, useRef } from 'react';

// Predefined list of QA/QC forms with their metadata
// These should match the files in public/pdfs/qaqc/
const QAQC_FORMS = [
  {
    id: 'material-receiving',
    name: 'Material Receiving Inspection',
    filename: 'material-receiving-inspection.pdf',
    description: 'Document material receiving inspections and verify quality',
    category: 'Inspection',
    icon: '📦'
  },
  {
    id: 'piping-material-checklist',
    name: 'Piping Material Receiving Checklist',
    filename: 'piping-material-checklist.pdf',
    description: 'Checklist for piping material receiving verification',
    category: 'Checklist',
    icon: '✅'
  },
  {
    id: 'weld-fitup-inspection',
    name: 'Weld Fit-Up & Visual Inspection',
    filename: 'weld-fitup-visual-inspection.pdf',
    description: 'Document weld fit-up and visual inspection results',
    category: 'Inspection',
    icon: '🔍'
  },
  {
    id: 'ndt-examination',
    name: 'NDT Weld Examination Report',
    filename: 'ndt-weld-examination.pdf',
    description: 'Non-destructive testing weld examination documentation',
    category: 'Testing',
    icon: '🔬'
  },
  {
    id: 'hydrostatic-test',
    name: 'Piping Hydrostatic Test Report',
    filename: 'piping-hydrostatic-test.pdf',
    description: 'Piping hydrostatic pressure test documentation',
    category: 'Testing',
    icon: '💧'
  },
  {
    id: 'pneumatic-test',
    name: 'Piping Pneumatic Test Report',
    filename: 'piping-pneumatic-test.pdf',
    description: 'Piping pneumatic pressure test documentation',
    category: 'Testing',
    icon: '💨'
  },
  {
    id: 'pre-commissioning',
    name: 'Pre-Commissioning Checklist',
    filename: 'pre-commissioning-checklist.pdf',
    description: 'Pre-commissioning verification checklist',
    category: 'Checklist',
    icon: '📋'
  },
  {
    id: 'mechanical-completion',
    name: 'Mechanical Completion Checklist',
    filename: 'mechanical-completion-checklist.pdf',
    description: 'Mechanical completion sign-off checklist',
    category: 'Checklist',
    icon: '⚙️'
  },
  {
    id: 'punch-list',
    name: 'Punch List / Deficiency List',
    filename: 'punch-list.pdf',
    description: 'Track punch list items and deficiencies',
    category: 'Tracking',
    icon: '📝'
  },
  {
    id: 'ncr',
    name: 'Non-Conformance Report (NCR)',
    filename: 'non-conformance-report.pdf',
    description: 'Document non-conformances and corrective actions',
    category: 'Report',
    icon: '⚠️'
  }
];

// Group forms by category
const groupFormsByCategory = (forms) => {
  return forms.reduce((acc, form) => {
    if (!acc[form.category]) {
      acc[form.category] = [];
    }
    acc[form.category].push(form);
    return acc;
  }, {});
};

const QAQCForms = ({ projectId, projectDetails }) => {
  const [selectedForm, setSelectedForm] = useState(null);
  const [formExists, setFormExists] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const iframeRef = useRef(null);

  // Check which PDFs exist
  useEffect(() => {
    const checkFormExistence = async () => {
      const existence = {};
      for (const form of QAQC_FORMS) {
        try {
          const response = await fetch(`/pdfs/qaqc/${form.filename}`, { method: 'HEAD' });
          existence[form.id] = response.ok;
        } catch {
          existence[form.id] = false;
        }
      }
      setFormExists(existence);
    };
    checkFormExistence();
  }, []);

  // Filter forms based on search and category
  const filteredForms = QAQC_FORMS.filter(form => {
    const matchesSearch = form.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          form.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || form.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const groupedForms = groupFormsByCategory(filteredForms);
  const categories = ['all', ...new Set(QAQC_FORMS.map(f => f.category))];

  const handlePrint = () => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.print();
    }
  };

  const handleDownload = () => {
    if (selectedForm) {
      const link = document.createElement('a');
      link.href = `/pdfs/qaqc/${selectedForm.filename}`;
      link.download = selectedForm.filename;
      link.click();
    }
  };

  const handleEmail = () => {
    if (selectedForm) {
      // Open default email client with form info
      const subject = encodeURIComponent(`QA/QC Form: ${selectedForm.name}`);
      const body = encodeURIComponent(
        `Please find attached the ${selectedForm.name} form.\n\n` +
        `Project: ${projectDetails?.job_number || ''} - ${projectDetails?.job_name || ''}\n\n` +
        `Note: Please download the PDF from the portal and attach it to this email.`
      );
      window.open(`mailto:?subject=${subject}&body=${body}`);
    }
  };

  // Form list view
  const FormList = () => (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ margin: 0, color: '#333', marginBottom: '0.5rem' }}>QA/QC Fillable Forms</h3>
        <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
          Select a form to view, fill, print, or download. Place your PDF files in <code>/public/pdfs/qaqc/</code>
        </p>
      </div>

      {/* Search and Filter */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        marginBottom: '1.5rem',
        flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="Search forms..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid #ddd',
            fontSize: '14px',
            flex: '1',
            minWidth: '200px'
          }}
        />
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid #ddd',
            fontSize: '14px',
            backgroundColor: 'white',
            cursor: 'pointer'
          }}
        >
          {categories.map(cat => (
            <option key={cat} value={cat}>
              {cat === 'all' ? 'All Categories' : cat}
            </option>
          ))}
        </select>
      </div>

      {/* Forms Grid */}
      {Object.entries(groupedForms).map(([category, forms]) => (
        <div key={category} style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ 
            margin: '0 0 0.75rem 0', 
            color: '#555', 
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            borderBottom: '1px solid #eee',
            paddingBottom: '0.5rem'
          }}>
            {category}
          </h4>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1rem'
          }}>
            {forms.map(form => {
              const exists = formExists[form.id];
              return (
                <div
                  key={form.id}
                  onClick={() => exists && setSelectedForm(form)}
                  style={{
                    padding: '1rem',
                    borderRadius: '8px',
                    border: exists ? '1px solid #ddd' : '1px dashed #ddd',
                    backgroundColor: exists ? 'white' : '#f9f9f9',
                    cursor: exists ? 'pointer' : 'not-allowed',
                    opacity: exists ? 1 : 0.6,
                    transition: 'all 0.2s ease',
                    boxShadow: exists ? '0 1px 3px rgba(0,0,0,0.05)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (exists) {
                      e.currentTarget.style.borderColor = '#dc2626';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (exists) {
                      e.currentTarget.style.borderColor = '#ddd';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <span style={{ fontSize: '24px' }}>{form.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: '600', 
                        color: '#333',
                        marginBottom: '4px',
                        fontSize: '14px'
                      }}>
                        {form.name}
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#666',
                        lineHeight: '1.4'
                      }}>
                        {form.description}
                      </div>
                      {!exists && (
                        <div style={{ 
                          marginTop: '8px',
                          fontSize: '11px',
                          color: '#f59e0b',
                          fontStyle: 'italic'
                        }}>
                          PDF not uploaded yet
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {filteredForms.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem', 
          color: '#666'
        }}>
          No forms found matching your criteria.
        </div>
      )}

      {/* Upload Instructions */}
      <div style={{ 
        marginTop: '2rem',
        padding: '1rem',
        backgroundColor: '#f0f9ff',
        borderRadius: '8px',
        border: '1px solid #bae6fd'
      }}>
        <h4 style={{ margin: '0 0 0.5rem 0', color: '#0369a1', fontSize: '14px' }}>
          📁 Adding New Forms
        </h4>
        <p style={{ margin: 0, fontSize: '13px', color: '#0c4a6e', lineHeight: '1.5' }}>
          To add fillable PDF forms, place them in the <code style={{ 
            backgroundColor: '#e0f2fe',
            padding: '2px 6px',
            borderRadius: '3px'
          }}>public/pdfs/qaqc/</code> folder with the exact filenames listed above.
          Contact your administrator for access.
        </p>
      </div>
    </div>
  );

  // PDF Viewer view
  const PDFViewer = () => (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: 'calc(100vh - 200px)',
      minHeight: '600px'
    }}>
      {/* Viewer Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '0.75rem 1rem',
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #ddd'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            onClick={() => setSelectedForm(null)}
            style={{
              padding: '6px 12px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            ← Back to Forms
          </button>
          <span style={{ fontSize: '20px' }}>{selectedForm?.icon}</span>
          <span style={{ fontWeight: '600', color: '#333' }}>{selectedForm?.name}</span>
        </div>
        
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={handlePrint}
            style={{
              padding: '6px 12px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#dc2626';
              e.target.style.color = 'white';
              e.target.style.borderColor = '#dc2626';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#f3f4f6';
              e.target.style.color = '#374151';
              e.target.style.borderColor = '#e5e7eb';
            }}
          >
            🖨️ Print
          </button>
          <button
            onClick={handleDownload}
            style={{
              padding: '6px 12px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#dc2626';
              e.target.style.color = 'white';
              e.target.style.borderColor = '#dc2626';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#f3f4f6';
              e.target.style.color = '#374151';
              e.target.style.borderColor = '#e5e7eb';
            }}
          >
            📥 Download
          </button>
          <button
            onClick={handleEmail}
            style={{
              padding: '6px 12px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#dc2626';
              e.target.style.color = 'white';
              e.target.style.borderColor = '#dc2626';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#f3f4f6';
              e.target.style.color = '#374151';
              e.target.style.borderColor = '#e5e7eb';
            }}
          >
            📧 Email
          </button>
        </div>
      </div>

      {/* PDF iframe */}
      <div style={{ flex: 1, backgroundColor: '#f5f5f5' }}>
        <iframe
          ref={iframeRef}
          src={`/pdfs/qaqc/${selectedForm?.filename}`}
          style={{
            width: '100%',
            height: '100%',
            border: 'none'
          }}
          title={selectedForm?.name}
        />
      </div>

      {/* Note about fillable PDFs */}
      <div style={{ 
        padding: '0.5rem 1rem',
        backgroundColor: '#fff7ed',
        borderTop: '1px solid #fed7aa',
        fontSize: '12px',
        color: '#9a3412',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <span>💡</span>
        <span>
          This PDF viewer supports fillable forms. Complete the form fields directly in the viewer, 
          then use Print or Download to save your filled form.
        </span>
      </div>
    </div>
  );

  return selectedForm ? <PDFViewer /> : <FormList />;
};

export default QAQCForms;

